/**
 * Tells the server this tab is alive, and which route it is on.
 *
 * Beats only while the tab is VISIBLE — a dashboard left open on a second monitor
 * overnight would otherwise report its owner as working all night, which is both wrong
 * and unfair to the person being looked at.
 *
 * Failures are swallowed on purpose: presence is ambient, and a red console error every
 * 25 seconds because Redis blinked would be worse than a missing dot.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import api from '@/services/api';

/** Matches the server's PRESENCE_TTL_MS / 3 — three misses before a user drops off. */
const BEAT_INTERVAL_MS = 25_000;

export function usePresenceHeartbeat(enabled: boolean) {
  // Pathname + query: the admin page turns this into a "jump to the exact
  // same view" link, so the filter state has to travel with the beat.
  const { pathname, search } = useLocation();
  const pathRef = useRef(pathname + search);
  pathRef.current = pathname + search;

  useEffect(() => {
    if (!enabled) return undefined;

    let stopped = false;

    const beat = () => {
      if (stopped || document.visibilityState !== 'visible') return;
      api.post('/presence/heartbeat', { path: pathRef.current }).catch(() => undefined);
    };

    // Beat immediately so a fresh sign-in or a route change shows up at once, then keep
    // the interval going for a tab that sits on one page.
    beat();
    const timer = window.setInterval(beat, BEAT_INTERVAL_MS);

    // Coming back to the tab should register straight away rather than up to 25s later.
    const onVisibility = () => beat();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, pathname, search]);

  // Signing out should drop the dot now, not leave a ghost for the whole TTL.
  useEffect(() => {
    if (enabled) return undefined;
    api.post('/presence/leave').catch(() => undefined);
    return undefined;
  }, [enabled]);
}

export default usePresenceHeartbeat;
